/**
 * Android install-prompt manager for the add-to-home-screen feature.
 * 「保存到桌面」的 Android 安装提示管理器。
 *
 * Chrome fires `beforeinstallprompt` at the WINDOW level once the injected
 * per-venue manifest satisfies installability (measured: ~300ms after the
 * manifest link lands, no service worker involved). The event can only be
 * consumed once, and it may arrive before any component is mounted — so this
 * module owns the whole lifecycle as a singleton: capture early, hold the
 * deferred event, hand it to whichever UI asks for it.
 *
 * Chrome 在注入的每店 manifest 满足安装条件后，会在 window 层面触发
 * `beforeinstallprompt`（实测注入后约 300ms，无需 service worker）。该事件只能
 * 消费一次，且可能早于任何组件挂载到达——所以本模块以单例形态接管整个生命周期：
 * 尽早捕获、持有 deferred 事件、交给需要它的 UI。
 *
 * iOS 没有对应机制（Apple 从未开放程序化触发，引导图是平台上限），本模块在
 * iOS 上永远处于「不可用」状态，无副作用。
 */

let deferredPrompt = null
let initialized = false
let availableCallbacks = []
let installedCallbacks = []
// 持有监听器引用，_resetForTests 时才能真正摘除，避免测试间累积
let handleBeforeInstallPrompt = null
let handleAppInstalled = null

/** Run subscriber callbacks defensively — a bad one must not break the rest. */
function notify(callbacks) {
  callbacks.slice().forEach(cb => {
    try {
      cb()
    } catch (e) {
      console.log('[a2hs] subscriber error', e)
    }
  })
}

/**
 * Attach the window listeners. Idempotent; safe to call from multiple places.
 * MUST run before (or in the same tick as) manifest injection, otherwise a
 * fast-firing event slips away unobserved.
 * 挂 window 监听。幂等，可多处调用。必须在注入 manifest 之前（或同一 tick 内）
 * 执行，否则事件来得快会漏掉。
 */
export function initInstallPromptCapture() {
  if (initialized) return
  if (typeof window === 'undefined') return
  initialized = true

  handleBeforeInstallPrompt = event => {
    // Suppress Chrome's own mini-infobar — the venue popup is the only UI.
    // 压掉 Chrome 自带的小横幅，弹窗是唯一入口。
    event.preventDefault()
    deferredPrompt = event
    notify(availableCallbacks)
  }
  handleAppInstalled = () => {
    deferredPrompt = null
    notify(installedCallbacks)
  }
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.addEventListener('appinstalled', handleAppInstalled)
}

/** Is a captured install prompt currently waiting to be shown? */
export function isInstallAvailable() {
  return deferredPrompt !== null
}

/**
 * Subscribe to "install became available". Fires immediately when the event
 * was already captured. Returns an unsubscribe function.
 * 订阅「安装已可用」。若事件已捕获则立即回调。返回取消订阅函数。
 */
export function onInstallAvailable(callback) {
  availableCallbacks.push(callback)
  if (deferredPrompt) notify([callback])
  return () => {
    availableCallbacks = availableCallbacks.filter(cb => cb !== callback)
  }
}

/**
 * Subscribe to "the app got installed". Returns an unsubscribe function.
 * 订阅「已完成安装」。返回取消订阅函数。
 */
export function onAppInstalled(callback) {
  installedCallbacks.push(callback)
  return () => {
    installedCallbacks = installedCallbacks.filter(cb => cb !== callback)
  }
}

/**
 * Show Chrome's native install confirmation. One-shot: the deferred event is
 * cleared no matter what the customer chooses (Chrome enforces single use;
 * a fresh event fires later if they dismissed).
 * 唤起 Chrome 原生安装确认框。一次性：无论顾客选什么都清空 deferred（Chrome
 * 强制单次使用；用户拒绝后浏览器稍后会再发新事件）。
 *
 * @returns {Promise<{outcome: string}>} 'accepted' | 'dismissed' | 'unavailable'
 */
export async function showInstallPrompt() {
  const event = deferredPrompt
  if (!event) return { outcome: 'unavailable' }
  deferredPrompt = null
  try {
    // prompt() 在新版 Chrome 返回 Promise，事件过期时异步 reject——必须 await，
    // 否则 rejection 逃出 try/catch 变成 unhandledrejection
    await event.prompt()
    const choice = await event.userChoice
    return { outcome: (choice && choice.outcome) || 'dismissed' }
  } catch (e) {
    // 事件过期或被重复消费——当作用户没装，保持沉默
    return { outcome: 'unavailable' }
  }
}

/**
 * Discard the captured event without consuming it. Called when the venue that
 * the event was captured under is no longer current — a deferred prompt has no
 * venue identity, and replaying venue A's event on venue B's page would show
 * B-branded UI that installs A's app.
 * 作废已捕获的事件（不消费）。deferred 事件不带 venue 身份，换店后必须作废，
 * 否则 B 店界面点下去装的是 A 店的 app。
 */
export function clearCapturedInstallPrompt() {
  deferredPrompt = null
}

/** Test helper: reset module state between jest cases. 测试助手，勿在业务代码调用。 */
export function _resetForTests() {
  if (typeof window !== 'undefined') {
    if (handleBeforeInstallPrompt) window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    if (handleAppInstalled) window.removeEventListener('appinstalled', handleAppInstalled)
  }
  handleBeforeInstallPrompt = null
  handleAppInstalled = null
  deferredPrompt = null
  initialized = false
  availableCallbacks = []
  installedCallbacks = []
}
